import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-4">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <div className="mb-8 text-center">
          <img src="/logo.png" alt="Forum Erkelenz MyTraining Logo" className="mx-auto mb-4 h-16 w-16 object-contain" data-testid="reset-password-logo" />
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-100">
            Neues Passwort
          </h1>
          <p className="mt-2 text-sm text-zinc-400">Wähle ein neues Passwort für dein Konto.</p>
        </div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6 shadow-sm sm:p-8">
          {!token ? (
            <p className="text-center text-sm text-destructive" data-testid="reset-password-invalid">
              Ungültiger Link. Bitte fordere einen neuen Link an.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
              <div className="space-y-2">
                <Label htmlFor="rp-password">Neues Passwort</Label>
                <Input
                  id="rp-password"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rp-confirm">Passwort bestätigen</Label>
                <Input
                  id="rp-confirm"
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-password-confirm-input"
                />
              </div>
              {error && (
                <p className="rounded-lg bg-red-950/50 px-3 py-2 text-sm text-destructive" data-testid="reset-password-error">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="reset-password-submit-button"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Passwort speichern
              </Button>
            </form>
          )}
        </div>
        <p className="mt-6 text-center text-sm text-zinc-400">
          <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="reset-back-to-login-link">
            Zurück zur Anmeldung
          </Link>
        </p>
      </div>
    </div>
  );
}
